import { columns } from "@/components/events/events.columns";
import { DataTable } from "@/components/table/table";
import { useEventsModal } from "@/hooks/useEventsModal";
import { useTRPC } from "@/lib/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "../ui/button";
import { EventsModalForm } from "./events-modal-form";

const eventFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string(),
  imageUrl: z.string(),
  allDay: z.boolean().default(false),
  start: z.string().datetime(),
  end: z.string().datetime(),
  calendarId: z.string().min(1, "Calendar is required"),
});

type EventFormValues = z.infer<typeof eventFormSchema>;

export function EventsTable() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data: events = [], isLoading } = useQuery(
    trpc.calendars.events.queryOptions({ calendarId: "all" }),
  );

  const { data: calendars = [] } = useQuery(trpc.calendars.list.queryOptions());

  const isModalOpen = useEventsModal((s) => s.isOpen);
  const setIsModalOpen = useEventsModal((s) => s.setIsModalOpen);
  const setEventId = useEventsModal((s) => s.setEventId);
  const eventId = useEventsModal((s) => s.eventId);
  const defaultValues = useEventsModal((s) => s.defaultValues);
  const setDefaultValues = useEventsModal((s) => s.setDefaultValues);

  const form = useForm<EventFormValues>({
    resolver: zodResolver(eventFormSchema),
    defaultValues: {
      title: "",
      description: "",
      imageUrl: "",
      allDay: false,
      start: new Date().toISOString(),
      end: new Date(Date.now() + 60 * 60 * 1000).toISOString(), // 1 hour later
      calendarId: "",
    },
  });

  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      form.reset({
        title: defaultValues.title || "",
        description: defaultValues.description || "",
        imageUrl: defaultValues.imageUrl || "",
        allDay: defaultValues.allDay || false,
        start: defaultValues.start || new Date().toISOString(),
        end:
          defaultValues.end ||
          new Date(Date.now() + 60 * 60 * 1000).toISOString(),
        calendarId: defaultValues.calendarId || "",
      });
    }
  }, [form, defaultValues]);

  const createMutation = useMutation(
    trpc.calendars.createEvent.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["calendars"] });
        toast.success("Event created successfully");
        setIsModalOpen(false);
        setEventId(null);
        form.reset();
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to create event");
      },
    }),
  );

  const updateMutation = useMutation(
    trpc.calendars.updateEvent.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["calendars"] });
        toast.success("Event updated successfully");
        setIsModalOpen(false);
        setEventId(null);
        form.reset();
      },
      onError: (error: unknown) => {
        toast.error((error as Error).message || "Failed to update event");
      },
    }),
  );

  const handleSubmit = (data: EventFormValues) => {
    if (eventId) {
      updateMutation.mutate({ id: eventId, ...data });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div className="flex flex-1 p-8 justify-around gap-8">
      <div className="flex flex-col space-y-8 w-full xl:w-3/4 lg:max-w-4xl xl:max-w-6xl">
        <div className="flex items-center justify-between space-y-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              Website Events
            </h2>
            <p className="text-muted-foreground">
              Manage events for the website calendar
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => {
                setDefaultValues({});
                form.reset({
                  title: "",
                  description: "",
                  imageUrl: "",
                  allDay: false,
                  start: new Date().toISOString(),
                  end: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
                  calendarId: calendars.length > 0 ? calendars[0].id : "",
                });
                setIsModalOpen(true);
              }}
            >
              New Event
            </Button>
          </div>
        </div>

        <EventsModalForm
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          form={form}
          onSubmit={handleSubmit}
          isLoading={createMutation.isPending || updateMutation.isPending}
          calendars={calendars}
        />

        {!isLoading && (
          <DataTable data={events} columns={columns} filterColumn="title" />
        )}
      </div>
    </div>
  );
}
